<script>
  import { onMount } from 'svelte';
  import { sessionStore } from '../stores';
  import { API_BASE_URL } from '../utils/config';
  import { auth } from '../firebase';
  import { onAuthStateChanged } from 'firebase/auth';

  // ── 상태 변수 ─────────────────────────────────────────
  let activeSection = 'overview';
  let apiKeys = [];
  let usageData = null;
  let serverStatus = null;
  let statusHistory = [];   // 상태 확인 이력 (최대 60회)
  let loading = false;
  let statusLoading = false;
  let errorMsg = '';
  let successMsg = '';

  // API 키 마스킹: 앞부분 숨기고 마지막 3글자만 노출
  function maskKey(key) {
    if (!key || key.length <= 3) return key;
    return 'erion-...' + key.slice(-3);
  }

  // 키 생성 폼
  let newKeyName = '';
  let creatingKey = false;

  // 복사 상태
  let copiedKeyId = null;
  let copiedCodeId = null;   // 코드 블록 복사 상태

  // 코드 예시 탭
  let codeTabStyle = 'python';    // python | curl | node
  let codeTabAdvanced = 'python';
  let codeTabSynth = 'python';
  let codeTabVideo = 'python';
  let codeTabObject = 'python';
  let codeTabBg    = 'python';

  // ── 헬퍼 ──────────────────────────────────────────────
  $: loggedIn = $sessionStore.loggedIn;

  async function getAuthHeaders() {
    if (!loggedIn) {
      throw new Error('로그인이 필요합니다.');
    }

    let user = auth.currentUser;
    if (!user) {
      user = await new Promise((resolve) => {
        let unsubscribe = () => {};
        const timeout = setTimeout(() => {
          unsubscribe();
          resolve(null);
        }, 1500);

        unsubscribe = onAuthStateChanged(auth, (nextUser) => {
          clearTimeout(timeout);
          unsubscribe();
          resolve(nextUser);
        });
      });
    }

    if (!user) {
      throw new Error('로그인 정보가 만료되었습니다. 다시 로그인해 주세요.');
    }

    const idToken = await user.getIdToken();
    return { Authorization: `Bearer ${idToken}` };
  }

  async function fetchApiKeys() {
    if (!loggedIn) return;
    loading = true;
    errorMsg = '';
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/user/api-keys`, { headers });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        throw new Error('서버에서 올바른 응답을 받지 못했습니다. 잠시 후 다시 시도해 주세요.');
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '조회 실패');
      apiKeys = data.api_keys || [];
    } catch (e) {
      errorMsg = e.message;
    } finally {
      loading = false;
    }
  }

  async function fetchUsage() {
    if (!loggedIn) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/user/api-keys/usage`, { headers });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('non-json response');
      const data = await res.json();
      if (res.ok) usageData = data;
      else usageData = { usage: [], monthly_total: 0 };
    } catch (e) {
      console.error('usage fetch failed', e);
      usageData = { usage: [], monthly_total: 0 };
    }
  }

  async function fetchStatusHistory() {
    // 페이지 로드 시 DB 이력을 가져와 타임라인 구성
    statusLoading = true;
    try {
      const res = await fetch(`${API_BASE_URL}/api/status/history`);
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('non-json');
      const data = await res.json();
      if (res.ok) {
        statusHistory = data.history || [];
        serverStatus  = data.latest  || null;
        // DB 이력이 아직 없으면 현재 상태만 조회해서 화면에 표시
        if (statusHistory.length === 0) {
          try {
            const currentRes = await fetch(`${API_BASE_URL}/api/status`);
            const currentCt = currentRes.headers.get('content-type') || '';
            if (currentRes.ok && currentCt.includes('application/json')) {
              serverStatus = await currentRes.json();
            }
          } catch (e) {
            console.error('initial status check failed', e);
          }
          return;
        }
      }
    } catch (e) {
      console.error('fetchStatusHistory failed', e);
    } finally {
      statusLoading = false;
    }
  }

  // ── 상태 타임라인 헬퍼 ──
  function getComponentList() {
    if (!serverStatus?.components) return [];
    return Object.entries(serverStatus.components).map(([key, comp]) => ({
      key,
      label: comp.label || key
    }));
  }

  function getPaddedBars(key, maxBars = 60) {
    const bars = statusHistory.map(h => ({
      status: key === '__overall__'
        ? (h.overall || 'unknown')
        : (h.components[key]?.status || 'unknown'),
      timestamp: h.timestamp
    }));
    const padCount = Math.max(0, maxBars - bars.length);
    return [...Array(padCount).fill(null), ...bars];
  }

  function latestComponentStatus(key) {
    return serverStatus?.components?.[key]?.status || 'unknown';
  }

  async function createKey() {
    if (!newKeyName.trim()) { errorMsg = '키 이름을 입력해 주세요.'; return; }
    creatingKey = true;
    errorMsg = '';
    successMsg = '';
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/user/api-keys`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ key_name: newKeyName.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '생성 실패');
      successMsg = `API 키 '${data.key_name}'이(가) 생성되었습니다.`;
      newKeyName = '';
      await fetchApiKeys();
    } catch (e) {
      errorMsg = e.message;
    } finally {
      creatingKey = false;
    }
  }

  async function deleteKey(keyId, keyName) {
    if (!confirm(`'${keyName}' 키를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) return;
    errorMsg = '';
    successMsg = '';
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/user/api-keys/${keyId}`, {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '삭제 실패');
      successMsg = '키가 삭제되었습니다.';
      await fetchApiKeys();
    } catch (e) {
      errorMsg = e.message;
    }
  }

  async function copyToClipboard(text, keyId) {
    try {
      await navigator.clipboard.writeText(text);
      copiedKeyId = keyId;
      setTimeout(() => { copiedKeyId = null; }, 2000);
    } catch (e) {
      // 폴백
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      copiedKeyId = keyId;
      setTimeout(() => { copiedKeyId = null; }, 2000);
    }
  }

  async function copyCode(text, id) {
    try {
      await navigator.clipboard.writeText(text);
      copiedCodeId = id;
      setTimeout(() => { copiedCodeId = null; }, 2000);
    } catch (e) {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      copiedCodeId = id;
      setTimeout(() => { copiedCodeId = null; }, 2000);
    }
  }

  function statusColor(s) {
    if (s === 'operational')          return '#22c55e';
    if (s === 'degraded')             return '#f59e0b';
    if (s === 'error' || s === 'down') return '#ef4444';
    return '#94a3b8';
  }
  function statusLabel(s) {
    if (s === 'operational') return '정상';
    if (s === 'degraded')    return '저하';
    return '알 수 없음';
  }

  function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('ko-KR');
  }

  // ── 코드 예시 ──────────────────────────────────────────
  const styleExamples = {
    python: `import requests, time

API_KEY = "여기에_발급받은_키_입력"
BASE_URL = "https://erion.wenit.co.kr"

# 1단계: 스타일 변환 요청
with open("my_dog.jpg", "rb") as img:
    res = requests.post(
        f"{BASE_URL}/api/v1/style-transform",
        headers={"X-API-Key": API_KEY},
        data={"style": "애니메이션(P)"},
        files={"image": img},
    )
res.raise_for_status()
task = res.json()
task_id = task["task_id"]
print(f"접수됨, task_id={task_id}")

# 2단계: 완료될 때까지 폴링 (최대 3분)
for _ in range(18):
    time.sleep(7)
    poll_res = requests.get(
        f"{BASE_URL}/api/v1/tasks/{task_id}",
        headers={"X-API-Key": API_KEY},
    )
    poll = poll_res.json()
    print(poll["status"])
    if poll["status"] == "completed":
        urls = poll.get("download_urls") or [poll["download_url"]]
        for i, url in enumerate(urls, 1):
            print(f"다운로드 URL {i}:", url)
        break
    if poll["status"] == "failed":
        print("처리 실패")
        break`,

    curl: `# 1단계: 요청 (task_id 받기)
curl -X POST https://erion.wenit.co.kr/api/v1/style-transform \\
  -H "X-API-Key: 여기에_발급받은_키_입력" \\
  -F "image=@my_dog.jpg" \\
  -F "style=애니메이션(P)"
# → {"task_id":"42","status":"queued","poll_url":"/api/v1/tasks/42",...}

# 2단계: 폴링 (completed가 될 때까지 반복)
curl https://erion.wenit.co.kr/api/v1/tasks/42 \\
  -H "X-API-Key: 여기에_발급받은_키_입력"
# → {
#      "task_id": 42,
#      "status": "completed",
#      "download_url": "https://.../downloads/ext_style_abc123.png",
#      "download_urls": [
#        "https://.../downloads/ext_style_abc123.png",
#        "https://.../downloads/result2_def456.png"
#      ]
#    }`,

    node: `import fetch from "node-fetch";
import FormData from "form-data";
import fs from "fs";

const API_KEY = "여기에_발급받은_키_입력";
const BASE = "https://erion.wenit.co.kr";

// 1단계: 스타일 변환 요청
const form = new FormData();
form.append("image", fs.createReadStream("my_dog.jpg"));
form.append("style", "애니메이션(P)");
const res = await fetch(\`\${BASE}/api/v1/style-transform\`, {
  method: "POST",
  headers: { "X-API-Key": API_KEY, ...form.getHeaders() },
  body: form,
});
const { task_id } = await res.json();

// 2단계: 폴링
for (let i = 0; i < 18; i++) {
  await new Promise(r => setTimeout(r, 7000));
  const r = await fetch(\`\${BASE}/api/v1/tasks/\${task_id}\`,
    { headers: { "X-API-Key": API_KEY } });
  const data = await r.json();
  if (data.status === "completed") {
    const urls = data.download_urls ?? [data.download_url];
    urls.forEach((url, i) => console.log(\`다운로드 URL \${i + 1}:\`, url));
    break;
  }
  if (data.status === "failed") { console.error("실패"); break; }
}`,
  };

  const advancedExamples = {
    python: `import requests, time

API_KEY = "여기에_발급받은_키_입력"
BASE_URL = "https://erion.wenit.co.kr"

with open("my_dog.jpg", "rb") as img:
    res = requests.post(
        f"{BASE_URL}/api/v1/advanced-style-transform",
        headers={"X-API-Key": API_KEY},
        data={
            "style": "이모티콘",
            # "subject_name": "Rion",  # 명품 잡지 포스터 사용 시 주석 해제
        },
        files={"image": img},
    )
res.raise_for_status()
task_id = res.json()["task_id"]

for _ in range(40):
    time.sleep(7)
    poll = requests.get(
        f"{BASE_URL}/api/v1/tasks/{task_id}",
        headers={"X-API-Key": API_KEY},
    ).json()
    print(poll["status"])
    if poll["status"] == "completed":
        print("다운로드 URL:", poll["download_url"])
        break`,

    curl: `curl -X POST https://erion.wenit.co.kr/api/v1/advanced-style-transform \\
  -H "X-API-Key: 여기에_발급받은_키_입력" \\
  -F "image=@my_dog.jpg" \\
  -F "style=이모티콘"
# 명품 잡지 포스터 사용 시 아래 줄의 #을 지우고, 위 style 값을 "명품 잡지 포스터"로 바꾸세요.
#  -F "subject_name=Rion"

curl https://erion.wenit.co.kr/api/v1/tasks/42 \\
  -H "X-API-Key: 여기에_발급받은_키_입력"`,

    node: `import fetch from "node-fetch";
import FormData from "form-data";
import fs from "fs";

const form = new FormData();
form.append("image", fs.createReadStream("my_dog.jpg"));
form.append("style", "이모티콘");
// 명품 잡지 포스터 사용 시 아래 줄의 주석을 해제하고 style을 "명품 잡지 포스터"로 바꾸세요.
// form.append("subject_name", "Rion");

const res = await fetch("https://erion.wenit.co.kr/api/v1/advanced-style-transform", {
  method: "POST",
  headers: { "X-API-Key": "여기에_발급받은_키_입력", ...form.getHeaders() },
  body: form,
});
console.log(await res.json());`,
  };

  const synthExamples = {
    python: `import requests

API_KEY = "여기에_발급받은_키_입력"
BASE_URL = "https://erion.wenit.co.kr"

files = {
    # AI 피팅의 경우 피사체 이미지와 액세서리 이미지의 순서가 중요하지만, 일반적인 합성일 경우에는 그렇게 중요하지 않음
    "image1": open("dog1.jpg", "rb"),      # 옷을 입힐 피사체 이미지
    "image2": open("outfit.jpg", "rb"),    # 입힐 옷 또는 액세서리 이미지
}
data = {
    "composite_mode": "ai_fitting",  # ai_fitting 또는 together
}

response = requests.post(
    f"{BASE_URL}/api/v1/image-synthesis",
    headers={"X-API-Key": API_KEY},
    data=data,
    files=files,
)
result = response.json()
if response.ok:
    print("다운로드 URL:", result["download_url"])
else:
    print("오류:", result.get("error"))`,

    curl: `# image1: 옷을 입힐 피사체 이미지
# image2: 입힐 옷 또는 액세서리 이미지
curl -X POST https://erion.wenit.co.kr/api/v1/image-synthesis \\
  -H "X-API-Key: 여기에_발급받은_키_입력" \\
  -F "image1=@dog1.jpg" \\
  -F "image2=@outfit.jpg" \\
  -F "composite_mode=ai_fitting"`,

    node: `import fetch from "node-fetch";
import FormData from "form-data";
import fs from "fs";

const form = new FormData();
// image1: 옷을 입힐 피사체 이미지
form.append("image1", fs.createReadStream("dog1.jpg"));
// image2: 입힐 옷 또는 액세서리 이미지
form.append("image2", fs.createReadStream("outfit.jpg"));
form.append("composite_mode", "ai_fitting");

const res = await fetch("https://erion.wenit.co.kr/api/v1/image-synthesis", {
  method: "POST",
  headers: { "X-API-Key": "여기에_발급받은_키_입력", ...form.getHeaders() },
  body: form,
});
console.log(await res.json());`,
  };

  // 응답 예시 JSON (복사 버튼용)
  const styleResponseJson = JSON.stringify({
    "1차 응답 (202 Accepted)": {
      task_id: "42",
      status: "queued",
      poll_url: "/api/v1/tasks/42",
      cost_rion: 1
    },
    "폴링 응답 GET /api/v1/tasks/42": {
      task_id: 42,
      status: "completed",
      download_url: "https://erion.wenit.co.kr/downloads/ext_style_abc123.png",
      download_urls: [
        "https://erion.wenit.co.kr/downloads/ext_style_abc123.png",
        "https://erion.wenit.co.kr/downloads/result2_def456.png"
      ]
    }
  }, null, 2);

  const advancedResponseJson = JSON.stringify({
    "1차 응답 (202 Accepted)": {
      task_id: "42",
      status: "queued",
      poll_url: "/api/v1/tasks/42",
      cost_rion: 1
    },
    "폴링 응답 GET /api/v1/tasks/42": {
      task_id: 42,
      status: "completed",
      download_url: "https://erion.wenit.co.kr/downloads/ext_advanced_style_abc123.png"
    }
  }, null, 2);

  const bgExamples = {
    python: `import requests, time

API_KEY = "여기에_발급받은_키_입력"
BASE_URL = "https://erion.wenit.co.kr"

# 1단계: 배경 제거 요청
with open("my_dog.jpg", "rb") as img:
    res = requests.post(
        f"{BASE_URL}/api/v1/remove-background",
        headers={"X-API-Key": API_KEY},
        data={"output_format": "png"},
        files={"image": img},
    )
res.raise_for_status()
task = res.json()
task_id = task["task_id"]
print(f"접수됨, task_id={task_id}")

# 2단계: 완료될 때까지 폴링 (최대 3분)
for _ in range(18):
    time.sleep(7)
    poll_res = requests.get(
        f"{BASE_URL}/api/v1/tasks/{task_id}",
        headers={"X-API-Key": API_KEY},
    )
    poll = poll_res.json()
    print(poll["status"])
    if poll["status"] == "completed":
        print("다운로드 URL:", poll["download_url"])
        break
    if poll["status"] == "failed":
        print("처리 실패")
        break`,

    curl: `# 1단계: 요청 (task_id 받기)
curl -X POST https://erion.wenit.co.kr/api/v1/remove-background \\
  -H "X-API-Key: 여기에_발급받은_키_입력" \\
  -F "image=@my_dog.jpg" \\
  -F "output_format=png"
# → {"task_id":"42","status":"queued","poll_url":"/api/v1/tasks/42",...}

# 2단계: 폴링 (completed가 될 때까지 반복)
curl https://erion.wenit.co.kr/api/v1/tasks/42 \\
  -H "X-API-Key: 여기에_발급받은_키_입력"
# → {
#      "task_id": 42,
#      "status": "completed",
#      "download_url": "https://.../downloads/ext_bg_abc123.png"
#    }`,

    node: `import fetch from "node-fetch";
import FormData from "form-data";
import fs from "fs";

const API_KEY = "여기에_발급받은_키_입력";
const BASE = "https://erion.wenit.co.kr";

// 1단계: 배경 제거 요청
const form = new FormData();
form.append("image", fs.createReadStream("my_dog.jpg"));
form.append("output_format", "png");
const res = await fetch(\`\${BASE}/api/v1/remove-background\`, {
  method: "POST",
  headers: { "X-API-Key": API_KEY, ...form.getHeaders() },
  body: form,
});
const { task_id } = await res.json();

// 2단계: 폴링
for (let i = 0; i < 18; i++) {
  await new Promise(r => setTimeout(r, 7000));
  const r = await fetch(\`\${BASE}/api/v1/tasks/\${task_id}\`,
    { headers: { "X-API-Key": API_KEY } });
  const data = await r.json();
  if (data.status === "completed") {
    console.log("다운로드 URL:", data.download_url);
    break;
  }
  if (data.status === "failed") { console.error("실패"); break; }
}`,
  };

  const videoExamples = {
    python: `import requests, time

API_KEY = "여기에_발급받은_키_입력"
BASE_URL = "https://erion.wenit.co.kr"

with open("my_dog.jpg", "rb") as img:
    res = requests.post(
        f"{BASE_URL}/api/v1/make-video",
        headers={"X-API-Key": API_KEY},
        data={"video_style": "kpop_dance"},
        files={"image": img},
    )
res.raise_for_status()
task_id = res.json()["task_id"]

for _ in range(90):
    time.sleep(10)
    poll = requests.get(
        f"{BASE_URL}/api/v1/tasks/{task_id}",
        headers={"X-API-Key": API_KEY},
    ).json()
    print(poll["status"])
    if poll["status"] == "completed":
        print("동영상 URL:", poll["download_url"])
        break`,

    curl: `curl -X POST https://erion.wenit.co.kr/api/v1/make-video \\
  -H "X-API-Key: 여기에_발급받은_키_입력" \\
  -F "image=@my_dog.jpg" \\
  -F "video_style=playful"`,

    node: `import fetch from "node-fetch";
import FormData from "form-data";
import fs from "fs";

const form = new FormData();
form.append("image", fs.createReadStream("my_dog.jpg"));
form.append("video_style", "walk");

const res = await fetch("https://erion.wenit.co.kr/api/v1/make-video", {
  method: "POST",
  headers: { "X-API-Key": "여기에_발급받은_키_입력", ...form.getHeaders() },
  body: form,
});
console.log(await res.json());`,
  };

  const objectExamples = {
    python: `import requests, time

API_KEY = "여기에_발급받은_키_입력"
BASE_URL = "https://erion.wenit.co.kr"

files = {
    "image": open("original.jpg", "rb"),
    "mask_image": open("mask.png", "rb"),
}
res = requests.post(
    f"{BASE_URL}/api/v1/object-remove",
    headers={"X-API-Key": API_KEY},
    files=files,
)
res.raise_for_status()
task_id = res.json()["task_id"]

for _ in range(30):
    time.sleep(7)
    poll = requests.get(
        f"{BASE_URL}/api/v1/tasks/{task_id}",
        headers={"X-API-Key": API_KEY},
    ).json()
    if poll["status"] == "completed":
        print("다운로드 URL:", poll["download_url"])
        break`,

    curl: `curl -X POST https://erion.wenit.co.kr/api/v1/object-remove \\
  -H "X-API-Key: 여기에_발급받은_키_입력" \\
  -F "image=@original.jpg" \\
  -F "mask_image=@mask.png"`,

    node: `import fetch from "node-fetch";
import FormData from "form-data";
import fs from "fs";

const form = new FormData();
form.append("image", fs.createReadStream("original.jpg"));
form.append("mask_image", fs.createReadStream("mask.png"));

const res = await fetch("https://erion.wenit.co.kr/api/v1/object-remove", {
  method: "POST",
  headers: { "X-API-Key": "여기에_발급받은_키_입력", ...form.getHeaders() },
  body: form,
});
console.log(await res.json());`,
  };

  const bgResponseJson = JSON.stringify({
    "1차 응답 (202 Accepted)": {
      task_id: "42",
      status: "queued",
      poll_url: "/api/v1/tasks/42",
      cost_rion: 1
    },
    "폴링 응답 GET /api/v1/tasks/42": {
      task_id: 42,
      status: "completed",
      download_url: "https://erion.wenit.co.kr/downloads/ext_bg_abc123.png"
    }
  }, null, 2);

  const synthResponseJson = JSON.stringify({
    message: "이미지 합성이 완료되었습니다.",
    download_url: "https://erion.wenit.co.kr/downloads/ext_synth_abc123.png",
    cost_rion: 1,
    composite_mode: "ai_fitting"
  }, null, 2);

  const videoResponseJson = JSON.stringify({
    "1차 응답 (202 Accepted)": {
      task_id: "42",
      status: "queued",
      poll_url: "/api/v1/tasks/42",
      cost_rion: 4
    },
    "폴링 응답 GET /api/v1/tasks/42": {
      task_id: 42,
      status: "completed",
      download_url: "https://erion.wenit.co.kr/downloads/ext_video_abc123.mp4"
    }
  }, null, 2);

  const objectResponseJson = JSON.stringify({
    "1차 응답 (202 Accepted)": {
      task_id: "42",
      status: "queued",
      poll_url: "/api/v1/tasks/42",
      cost_rion: 1
    },
    "폴링 응답 GET /api/v1/tasks/42": {
      task_id: 42,
      status: "completed",
      download_url: "https://erion.wenit.co.kr/downloads/ext_object_remove_abc123.png"
    }
  }, null, 2);

  onMount(() => {
    fetchStatusHistory();   // 서버 상태 이력 로드 (DB 기반)
    if (loggedIn) {
      fetchApiKeys();
      fetchUsage();
    }
    // 클라이언트 폴링 없음 — 서버 APScheduler가 2시간마다 DB에 기록
  });
</script>

<div class="api-docs-page">
  <!-- ─── 사이드 네비게이션 ─── -->
  <aside class="sidebar">
    <h2 class="sidebar-title">API 문서</h2>
    <nav>
      <button class:active={activeSection==='overview'} on:click={() => activeSection='overview'}>
        📄 개요
      </button>
      <button class:active={activeSection==='style'} on:click={() => activeSection='style'}>
        🎨 스타일 변환 API
      </button>
      <button class:active={activeSection==='advanced'} on:click={() => activeSection='advanced'}>
        ✨ 고급 스타일 변환 API
      </button>
      <button class:active={activeSection==='synth'} on:click={() => activeSection='synth'}>
        🖼️ 이미지 합성 API
      </button>
      <button class:active={activeSection==='video'} on:click={() => activeSection='video'}>
        🎬 동영상 생성 API
      </button>
      <button class:active={activeSection==='object'} on:click={() => activeSection='object'}>
        🧽 객체 지우기 API
      </button>
      <button class:active={activeSection==='bg'} on:click={() => activeSection='bg'}>
        ✂️ 배경 제거 API
      </button>
      <button class:active={activeSection==='keys'} on:click={() => { activeSection='keys'; fetchApiKeys(); }}>
        🔑 내 API 키
      </button>
      <button class:active={activeSection==='usage'} on:click={() => { activeSection='usage'; fetchUsage(); fetchApiKeys(); }}>
        📊 사용량 대시보드
      </button>
      <button class:active={activeSection==='status'} on:click={() => activeSection='status'}>
        🟢 서버 상태
      </button>
    </nav>
  </aside>

  <!-- ─── 메인 콘텐츠 ─── -->
  <main class="doc-content">

    <!-- ═══ 개요 섹션 ═══ -->
    {#if activeSection === 'overview'}
      <section>
        <h1>이리온 API 소개</h1>
        <p class="lead">
          이리온 API를 사용하면 <strong>스타일 변환</strong>, <strong>고급 스타일 변환</strong>, <strong>이미지 합성</strong>,
          <strong>동영상 생성</strong>, <strong>객체 지우기</strong>, <strong>배경 제거</strong> 기능을
          여러분의 서비스·스크립트에 직접 통합할 수 있습니다.
        </p>

        <div class="info-grid">
          <div class="info-card">
            <div class="info-icon">🔑</div>
            <h3>API 키 기반 인증</h3>
            <p>발급받은 API 키를 <code>X-API-Key</code> 헤더로 전달하세요.</p>
          </div>
          <div class="info-card">
            <div class="info-icon">⏱️</div>
            <h3>분당 요청 한도</h3>
            <p>기본 10 RPM. 작업 상태 폴링은 요청 한도에 포함되지 않습니다.</p>
          </div>
          <div class="info-card">
            <div class="info-icon">💎</div>
            <h3>RION 소비</h3>
            <p>API를 통한 요청도 웹 편집기와 동일한 양의 RION을 소모합니다.</p>
          </div>
          <div class="info-card">
            <div class="info-icon">🗝️</div>
            <h3>키 제한</h3>
            <p>계정당 최대 3개의 API 키를 생성할 수 있습니다.</p>
          </div>
        </div>

        <h2>기본 요청 구조</h2>
        <div class="code-block">
          <pre>POST https://erion.wenit.co.kr/api/v1/&lt;기능&gt;
Content-Type: multipart/form-data
X-API-Key: 여기에_발급받은_키_입력</pre>
        </div>

        <h2>응답 코드</h2>
        <table class="response-table">
          <thead><tr><th>코드</th><th>의미</th></tr></thead>
          <tbody>
            <tr><td><span class="badge green">200</span></td><td>성공 (이미지 합성 등 즉시 처리)</td></tr>
            <tr><td><span class="badge blue">202</span></td><td>요청 접수됨 (스타일·동영상·객체 지우기 등 비동기 처리)</td></tr>
            <tr><td><span class="badge red">401</span></td><td>API 키 없음</td></tr>
            <tr><td><span class="badge red">402</span></td><td>RION 부족</td></tr>
            <tr><td><span class="badge red">403</span></td><td>유효하지 않은 API 키</td></tr>
            <tr><td><span class="badge yellow">429</span></td><td>RPM 한도 초과</td></tr>
            <tr><td><span class="badge red">500</span></td><td>서버 오류</td></tr>
          </tbody>
        </table>
      </section>

    <!-- ═══ 스타일 변환 API ═══ -->
    {:else if activeSection === 'style'}
      <section>
        <h1>🎨 스타일 변환 API</h1>
        <p class="lead">반려동물 이미지를 일반 스타일로 변환합니다. 고급 스타일은 별도 엔드포인트를 사용하며, 결과는 폴링으로 확인하세요.</p>

        <div class="endpoint-box">
          <span class="method post">POST</span>
          <code>/api/v1/style-transform</code>
        </div>

        <h2>요청 파라미터</h2>
        <table class="param-table">
          <thead><tr><th>파라미터</th><th>위치</th><th>필수</th><th>설명</th></tr></thead>
          <tbody>
            <tr><td><code>X-API-Key</code></td><td>헤더</td><td><span class="required">필수</span></td><td>발급받은 API 키</td></tr>
            <tr><td><code>image</code></td><td>form-data</td><td><span class="required">필수</span></td><td>변환할 이미지 파일 (jpg, png 등)</td></tr>
            <tr><td><code>style</code></td><td>form-data</td><td><span class="required">필수</span></td><td>스타일 이름 (예: "애니메이션(P)")</td></tr>
            <tr><td><code>output_format</code></td><td>form-data</td><td>선택</td><td>결과 파일 형식 (기본값: png)</td></tr>
          </tbody>
        </table>

        <h2>사용 가능한 주요 스타일</h2>
        <div class="style-chips">
          {#each ['애니메이션(P)', '수채화', '점묘화', '라인드로잉', '플랫 디자인', '흑백 필름 누아르', '팝 아트', '픽셀 아트', '고전 비디오 게임', '아메리칸 코믹북', '애니메이션(Gh)', '폴리곤 아트', '피규어', '치비 니트 인형', '종이접기', '인형놀이', '거품목욕', '휴식', '피크닉', '낮잠', '캠핑', '비오는 날 창가', '시네마틱 초상화', '스튜디오(SNS)', '스튜디오(감성)', '스튜디오(광고)', '스튜디오(아트)', '즉석 사진', '봄', '여름', '가을', '겨울'] as s}
            <span class="chip">{s}</span>
          {/each}
        </div>

        <h2>코드 예시</h2>
        <div class="code-panel">
          <div class="code-tabs">
            <button class:tab-active={codeTabStyle==='python'} on:click={() => codeTabStyle='python'}>Python</button>
            <button class:tab-active={codeTabStyle==='curl'}   on:click={() => codeTabStyle='curl'}>cURL</button>
            <button class:tab-active={codeTabStyle==='node'}   on:click={() => codeTabStyle='node'}>Node.js</button>
            <button class="copy-btn-tab" on:click={() => copyCode(styleExamples[codeTabStyle], 'style-code')}>
              {copiedCodeId === 'style-code' ? '✓ 복사됨' : '📋 복사'}
            </button>
          </div>
          <div class="code-block">
            <pre>{styleExamples[codeTabStyle]}</pre>
          </div>
        </div>

        <h2>응답 예시</h2>
        <div class="code-block-wrapper">
          <button class="copy-btn" on:click={() => copyCode(styleResponseJson, 'style-resp')}>
            {copiedCodeId === 'style-resp' ? '✓ 복사됨' : '📋 복사'}
          </button>
          <div class="code-block">
            <pre>{styleResponseJson}</pre>
          </div>
        </div>
      </section>

    <!-- ═══ 고급 스타일 변환 API ═══ -->
    {:else if activeSection === 'advanced'}
      <section>
        <h1>✨ 고급 스타일 변환 API</h1>
        <p class="lead">이모티콘, 실사 이모티콘, 인포그래픽, 명품 잡지 포스터처럼 더 복잡한 스타일 변환을 별도 엔드포인트에서 처리합니다.</p>

        <div class="endpoint-box">
          <span class="method post">POST</span>
          <code>/api/v1/advanced-style-transform</code>
        </div>

        <h2>요청 파라미터</h2>
        <table class="param-table">
          <thead><tr><th>파라미터</th><th>위치</th><th>필수</th><th>설명</th></tr></thead>
          <tbody>
            <tr><td><code>X-API-Key</code></td><td>헤더</td><td><span class="required">필수</span></td><td>발급받은 API 키</td></tr>
            <tr><td><code>image</code></td><td>form-data</td><td><span class="required">필수</span></td><td>변환할 이미지 파일</td></tr>
            <tr><td><code>style</code></td><td>form-data</td><td><span class="required">필수</span></td><td>고급 스타일 이름</td></tr>
            <tr><td><code>subject_name</code></td><td>form-data</td><td>조건부</td><td><code>명품 잡지 포스터</code> 스타일에서 사용할 피사체 이름</td></tr>
            <tr><td><code>output_format</code></td><td>form-data</td><td>선택</td><td>결과 파일 형식 (기본값: png)</td></tr>
          </tbody>
        </table>

        <h2>사용 가능한 고급 스타일</h2>
        <div class="style-chips">
          {#each ['이모티콘', '실사 이모티콘', '인포그래픽', '명품 잡지 포스터'] as s}
            <span class="chip">{s}</span>
          {/each}
        </div>

        <h2>코드 예시</h2>
        <div class="code-panel">
          <div class="code-tabs">
            <button class:tab-active={codeTabAdvanced==='python'} on:click={() => codeTabAdvanced='python'}>Python</button>
            <button class:tab-active={codeTabAdvanced==='curl'}   on:click={() => codeTabAdvanced='curl'}>cURL</button>
            <button class:tab-active={codeTabAdvanced==='node'}   on:click={() => codeTabAdvanced='node'}>Node.js</button>
            <button class="copy-btn-tab" on:click={() => copyCode(advancedExamples[codeTabAdvanced], 'advanced-code')}>
              {copiedCodeId === 'advanced-code' ? '✓ 복사됨' : '📋 복사'}
            </button>
          </div>
          <div class="code-block">
            <pre>{advancedExamples[codeTabAdvanced]}</pre>
          </div>
        </div>

        <h2>응답 예시</h2>
        <div class="code-block-wrapper">
          <button class="copy-btn" on:click={() => copyCode(advancedResponseJson, 'advanced-resp')}>
            {copiedCodeId === 'advanced-resp' ? '✓ 복사됨' : '📋 복사'}
          </button>
          <div class="code-block">
            <pre>{advancedResponseJson}</pre>
          </div>
        </div>
      </section>

    <!-- ═══ 이미지 합성 API ═══ -->
    {:else if activeSection === 'synth'}
      <section>
        <h1>🖼️ 이미지 합성 API</h1>
        <p class="lead">원본 이미지와 합성용 이미지를 Flutter 앱과 동일한 두 가지 합성 방식 중 하나로 처리합니다. 사용자가 별도 프롬프트를 입력하지 않습니다.</p>

        <div class="endpoint-box">
          <span class="method post">POST</span>
          <code>/api/v1/image-synthesis</code>
        </div>

        <h2>요청 파라미터</h2>
        <table class="param-table">
          <thead><tr><th>파라미터</th><th>위치</th><th>필수</th><th>설명</th></tr></thead>
          <tbody>
            <tr><td><code>X-API-Key</code></td><td>헤더</td><td><span class="required">필수</span></td><td>발급받은 API 키</td></tr>
            <tr><td><code>image1</code></td><td>form-data</td><td><span class="required">필수</span></td><td>첫 번째 이미지 파일</td></tr>
            <tr><td><code>image2</code></td><td>form-data</td><td><span class="required">필수</span></td><td>합성할 두 번째 이미지</td></tr>
            <tr><td><code>composite_mode</code></td><td>form-data</td><td><span class="required">필수</span></td><td><code>ai_fitting</code> 또는 <code>together</code></td></tr>
          </tbody>
        </table>

        <h2>합성 방식</h2>
        <div class="style-chips">
          <span class="chip"><code>ai_fitting</code> AI 피팅</span>
          <span class="chip"><code>together</code> 함께 합성</span>
        </div>

        <h2>코드 예시</h2>
        <div class="code-panel">
          <div class="code-tabs">
            <button class:tab-active={codeTabSynth==='python'} on:click={() => codeTabSynth='python'}>Python</button>
            <button class:tab-active={codeTabSynth==='curl'}   on:click={() => codeTabSynth='curl'}>cURL</button>
            <button class:tab-active={codeTabSynth==='node'}   on:click={() => codeTabSynth='node'}>Node.js</button>
            <button class="copy-btn-tab" on:click={() => copyCode(synthExamples[codeTabSynth], 'synth-code')}>
              {copiedCodeId === 'synth-code' ? '✓ 복사됨' : '📋 복사'}
            </button>
          </div>
          <div class="code-block">
            <pre>{synthExamples[codeTabSynth]}</pre>
          </div>
        </div>

        <h2>응답 예시</h2>
        <div class="code-block-wrapper">
          <button class="copy-btn" on:click={() => copyCode(synthResponseJson, 'synth-resp')}>
            {copiedCodeId === 'synth-resp' ? '✓ 복사됨' : '📋 복사'}
          </button>
          <div class="code-block">
            <pre>{synthResponseJson}</pre>
          </div>
        </div>
      </section>

    <!-- ═══ 동영상 생성 API ═══ -->
    {:else if activeSection === 'video'}
      <section>
        <h1>🎬 동영상 생성 API</h1>
        <p class="lead">원본 이미지 속 피사체를 Flutter 앱과 동일한 세 가지 유형 중 하나로 약 8초 분량의 동영상으로 생성합니다.</p>

        <div class="endpoint-box">
          <span class="method post">POST</span>
          <code>/api/v1/make-video</code>
        </div>

        <h2>요청 파라미터</h2>
        <table class="param-table">
          <thead><tr><th>파라미터</th><th>위치</th><th>필수</th><th>설명</th></tr></thead>
          <tbody>
            <tr><td><code>X-API-Key</code></td><td>헤더</td><td><span class="required">필수</span></td><td>발급받은 API 키</td></tr>
            <tr><td><code>image</code></td><td>form-data</td><td><span class="required">필수</span></td><td>동영상으로 만들 원본 이미지</td></tr>
            <tr><td><code>video_style</code></td><td>form-data</td><td><span class="required">필수</span></td><td><code>kpop_dance</code>, <code>walk</code>, <code>playful</code> 중 하나</td></tr>
          </tbody>
        </table>

        <h2>동영상 유형</h2>
        <div class="style-chips">
          <span class="chip"><code>kpop_dance</code> K-POP 댄스</span>
          <span class="chip"><code>walk</code> 걷는 모습</span>
          <span class="chip"><code>playful</code> 재롱부리기</span>
        </div>

        <h2>코드 예시</h2>
        <div class="code-panel">
          <div class="code-tabs">
            <button class:tab-active={codeTabVideo==='python'} on:click={() => codeTabVideo='python'}>Python</button>
            <button class:tab-active={codeTabVideo==='curl'}   on:click={() => codeTabVideo='curl'}>cURL</button>
            <button class:tab-active={codeTabVideo==='node'}   on:click={() => codeTabVideo='node'}>Node.js</button>
            <button class="copy-btn-tab" on:click={() => copyCode(videoExamples[codeTabVideo], 'video-code')}>
              {copiedCodeId === 'video-code' ? '✓ 복사됨' : '📋 복사'}
            </button>
          </div>
          <div class="code-block">
            <pre>{videoExamples[codeTabVideo]}</pre>
          </div>
        </div>

        <h2>응답 예시</h2>
        <div class="code-block-wrapper">
          <button class="copy-btn" on:click={() => copyCode(videoResponseJson, 'video-resp')}>
            {copiedCodeId === 'video-resp' ? '✓ 복사됨' : '📋 복사'}
          </button>
          <div class="code-block">
            <pre>{videoResponseJson}</pre>
          </div>
        </div>
      </section>

    <!-- ═══ 객체 지우기 API ═══ -->
    {:else if activeSection === 'object'}
      <section>
        <h1>🧽 객체 지우기 API</h1>
        <p class="lead">원본 이미지와 마스킹 이미지를 받아 Flutter 앱의 객체 지우기와 같은 방식으로 마스크 영역을 자연스럽게 복원합니다.</p>

        <div class="endpoint-box">
          <span class="method post">POST</span>
          <code>/api/v1/object-remove</code>
        </div>

        <h2>요청 파라미터</h2>
        <table class="param-table">
          <thead><tr><th>파라미터</th><th>위치</th><th>필수</th><th>설명</th></tr></thead>
          <tbody>
            <tr><td><code>X-API-Key</code></td><td>헤더</td><td><span class="required">필수</span></td><td>발급받은 API 키</td></tr>
            <tr><td><code>image</code></td><td>form-data</td><td><span class="required">필수</span></td><td>원본 이미지 파일</td></tr>
            <tr><td><code>mask_image</code></td><td>form-data</td><td><span class="required">필수</span></td><td>지울 영역이 표시된 마스크 이미지 파일</td></tr>
          </tbody>
        </table>

        <div class="info-note">
          <strong>마스크 방식:</strong> Flutter 앱처럼 마스크의 알파 영역을 제거 대상으로 사용합니다.
          마스크 이미지는 원본과 같은 크기를 권장하며, 크기가 달라도 서버에서 원본 크기에 맞춰 처리합니다.
        </div>

        <h2>코드 예시</h2>
        <div class="code-panel">
          <div class="code-tabs">
            <button class:tab-active={codeTabObject==='python'} on:click={() => codeTabObject='python'}>Python</button>
            <button class:tab-active={codeTabObject==='curl'}   on:click={() => codeTabObject='curl'}>cURL</button>
            <button class:tab-active={codeTabObject==='node'}   on:click={() => codeTabObject='node'}>Node.js</button>
            <button class="copy-btn-tab" on:click={() => copyCode(objectExamples[codeTabObject], 'object-code')}>
              {copiedCodeId === 'object-code' ? '✓ 복사됨' : '📋 복사'}
            </button>
          </div>
          <div class="code-block">
            <pre>{objectExamples[codeTabObject]}</pre>
          </div>
        </div>

        <h2>응답 예시</h2>
        <div class="code-block-wrapper">
          <button class="copy-btn" on:click={() => copyCode(objectResponseJson, 'object-resp')}>
            {copiedCodeId === 'object-resp' ? '✓ 복사됨' : '📋 복사'}
          </button>
          <div class="code-block">
            <pre>{objectResponseJson}</pre>
          </div>
        </div>
      </section>

    <!-- ═══ 배경 제거 API ═══ -->
    {:else if activeSection === 'bg'}
      <section>
        <h1>✂️ 배경 제거 API</h1>
        <p class="lead">이미지의 배경을 자동으로 제거하여 피사체만 남깁니다. 비동기로 처리되며, 결과는 폴링 엔드포인트에서 확인하세요.</p>

        <div class="endpoint-box">
          <span class="method post">POST</span>
          <code>/api/v1/remove-background</code>
        </div>

        <h2>요청 파라미터</h2>
        <table class="param-table">
          <thead><tr><th>파라미터</th><th>위치</th><th>필수</th><th>설명</th></tr></thead>
          <tbody>
            <tr><td><code>X-API-Key</code></td><td>헤더</td><td><span class="required">필수</span></td><td>발급받은 API 키</td></tr>
            <tr><td><code>image</code></td><td>form-data</td><td><span class="required">필수</span></td><td>배경을 제거할 이미지 파일 (jpg, png 등)</td></tr>
            <tr><td><code>output_format</code></td><td>form-data</td><td>선택</td><td>결과 파일 형식 (기본값: png, webp 가능)</td></tr>
          </tbody>
        </table>

        <h2>처리 방식 안내</h2>
        <div class="info-note">
          <strong>⏱️ 비동기 처리:</strong> 배경 제거는 스타일 변환과 동일하게 비동기 처리됩니다.
          첫 번째 응답(202)에서 <code>task_id</code>를 받은 뒤,
          <code>GET /api/v1/tasks/{'{task_id}'}</code>를 폴링하여 결과 URL을 확인하세요.
          완료된 이미지는 배경이 透明(투명)하게 처리된 PNG 파일입니다.
        </div>

        <h2>코드 예시</h2>
        <div class="code-panel">
          <div class="code-tabs">
            <button class:tab-active={codeTabBg==='python'} on:click={() => codeTabBg='python'}>Python</button>
            <button class:tab-active={codeTabBg==='curl'}   on:click={() => codeTabBg='curl'}>cURL</button>
            <button class:tab-active={codeTabBg==='node'}   on:click={() => codeTabBg='node'}>Node.js</button>
            <button class="copy-btn-tab" on:click={() => copyCode(bgExamples[codeTabBg], 'bg-code')}>
              {copiedCodeId === 'bg-code' ? '✓ 복사됨' : '📋 복사'}
            </button>
          </div>
          <div class="code-block">
            <pre>{bgExamples[codeTabBg]}</pre>
          </div>
        </div>

        <h2>응답 예시</h2>
        <div class="code-block-wrapper">
          <button class="copy-btn" on:click={() => copyCode(bgResponseJson, 'bg-resp')}>
            {copiedCodeId === 'bg-resp' ? '✓ 복사됨' : '📋 복사'}
          </button>
          <div class="code-block">
            <pre>{bgResponseJson}</pre>
          </div>
        </div>
      </section>

    <!-- ═══ 내 API 키 ═══ -->
    {:else if activeSection === 'keys'}
      <section>
        <h1>🔑 내 API 키 관리</h1>

        {#if !loggedIn}
          <div class="alert info">API 키를 관리하려면 로그인이 필요합니다.</div>
        {:else}

          {#if errorMsg}
            <div class="alert error">{errorMsg}</div>
          {/if}
          {#if successMsg}
            <div class="alert success">{successMsg}</div>
          {/if}

          <!-- 키 생성 폼 -->
          <div class="create-key-card">
            <h2>새 API 키 생성 ({apiKeys.length}/3)</h2>
            <p class="hint">API 키 이름은 본인이 알아보기 쉽도록 자유롭게 설정할 수 있습니다.</p>
            <div class="create-key-form">
              <input
                type="text"
                placeholder="키 이름 입력 (예: 내 프로젝트 키)"
                bind:value={newKeyName}
                maxlength="100"
                disabled={apiKeys.length >= 3 || creatingKey}
                on:keydown={(e) => e.key === 'Enter' && createKey()}
              />
              <button
                class="btn-primary"
                on:click={createKey}
                disabled={apiKeys.length >= 3 || creatingKey || !newKeyName.trim()}
              >
                {creatingKey ? '생성 중...' : '키 생성'}
              </button>
            </div>
            {#if apiKeys.length >= 3}
              <p class="warn-text">최대 3개의 API 키를 생성할 수 있습니다. 더 필요하시면 기존 키를 삭제해 주세요.</p>
            {/if}
          </div>

          <!-- 키 목록 -->
          {#if loading}
            <div class="loading">불러오는 중...</div>
          {:else if apiKeys.length === 0}
            <div class="empty-state">아직 생성된 API 키가 없습니다.</div>
          {:else}
            <div class="key-list">
              {#each apiKeys as k (k.id)}
                <div class="key-card">
                  <span class="key-name">{k.key_name}</span>
                  <code class="key-value">{maskKey(k.api_key)}</code>
                  <button
                    class="btn-icon"
                    title="클립보드에 복사"
                    on:click={() => copyToClipboard(k.api_key, k.id)}
                  >
                    {copiedKeyId === k.id ? '✓ 복사됨' : '📋 복사'}
                  </button>
                  <span class="key-meta">RPM {k.rpm_limit} · {formatDate(k.created_at)} 생성</span>
                  <button
                    class="btn-danger"
                    on:click={() => deleteKey(k.id, k.key_name)}
                  >
                    삭제
                  </button>
                </div>
              {/each}
            </div>
          {/if}

          <div class="info-note">
            <strong>💡 보안 안내:</strong> API 키를 공개 저장소나 클라이언트 코드에 직접 노출하지 마세요.
            키가 유출된 경우 즉시 삭제하고 새 키를 발급받으세요.
          </div>
        {/if}
      </section>

    <!-- ═══ 사용량 대시보드 ═══ -->
    {:else if activeSection === 'usage'}
      <section>
        <h1>📊 API 사용량 대시보드</h1>

        {#if !loggedIn}
          <div class="alert info">사용량을 확인하려면 로그인이 필요합니다.</div>
        {:else if !usageData || loading}
          <div class="loading">불러오는 중...</div>
        {:else if apiKeys.length === 0}
          <div class="no-keys-state">
            <div class="no-keys-icon">🔑</div>
            <h3>아직 생성된 API 키가 없습니다</h3>
            <p>API 키를 만들면 이곳에서 호출 기록과 사용량을 확인할 수 있습니다.</p>
            <button class="btn-primary" on:click={() => { activeSection = 'keys'; }}>
              API 키 만들러 가기
            </button>
          </div>
        {:else}

          <!-- 월간 요약 -->
          <div class="stats-row">
            <div class="stat-card">
              <div class="stat-value">{usageData.monthly_total ?? 0}</div>
              <div class="stat-label">이번 달 API 호출</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{apiKeys.length}</div>
              <div class="stat-label">사용 중인 API 키</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{apiKeys.length > 0 ? apiKeys[0].rpm_limit : 10}</div>
              <div class="stat-label">기본 RPM 한도</div>
            </div>
          </div>

          <!-- 최근 사용 기록 -->
          {#if usageData.usage && usageData.usage.length > 0}
            <h2>최근 외부 API 사용 기록</h2>
            <table class="usage-table">
              <thead>
                <tr>
                  <th>날짜</th>
                  <th>기능</th>
                  <th>호출 수</th>
                </tr>
              </thead>
              <tbody>
                {#each usageData.usage as row}
                  <tr>
                    <td>{row.date}</td>
                    <td>
                      {#if row.function_name === 'style_transform'}
                        🎨 스타일 변환
                      {:else if row.function_name === 'advanced_style_transform'}
                        ✨ 고급 스타일 변환
                      {:else if row.function_name === 'image_composite'}
                        🖼️ 이미지 합성
                      {:else if row.function_name === 'make_video'}
                        🎬 동영상 생성
                      {:else if row.function_name === 'object_remove'}
                        🧽 객체 지우기
                      {:else if row.function_name === 'remove_background'}
                        ✂️ 배경 제거
                      {:else}
                        {row.function_name}
                      {/if}
                    </td>
                    <td>{row.count}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {:else}
            <div class="empty-state">아직 외부 API 사용 기록이 없습니다.</div>
          {/if}

          <div class="info-note">
            <strong>📌 안내:</strong> 이 대시보드는 외부 API 키를 통한 호출 내역만 표시합니다.
            웹 편집기 사용 기록은 대시보드 페이지에서 확인하실 수 있습니다.
          </div>
        {/if}
      </section>

    <!-- ═══ 서버 상태 ═══ -->
    {:else if activeSection === 'status'}
      <section>
        <h1>🟢 서버 상태</h1>
        <p class="lead">이리온 서비스의 각 구성요소 상태 이력입니다. 서버가 2시간마다 자동으로 측정하여 기록하며, 페이지를 새로고침해도 과거 기록이 유지됩니다.</p>

        {#if statusLoading && statusHistory.length === 0}
          <div class="loading">상태 확인 중...</div>
        {:else if serverStatus || statusHistory.length > 0}

          <!-- 전체 상태 배너 -->
          <div class="overall-status" style="border-color: {statusColor(serverStatus?.overall ?? 'unknown')}">
            <span class="overall-dot" style="background: {statusColor(serverStatus?.overall ?? 'unknown')}"></span>
            <span class="overall-text">
              {#if serverStatus?.overall === 'operational'}
                모든 시스템이 정상적으로 동작하고 있습니다.
              {:else if serverStatus?.overall === 'degraded'}
                일부 서비스에 이상이 감지되었습니다.
              {:else}
                상태를 확인할 수 없습니다.
              {/if}
            </span>
            <span class="updated-at">
              마지막 확인: {serverStatus ? new Date(serverStatus.timestamp).toLocaleString('ko-KR') : '-'}
            </span>
          </div>

          <!-- 타임라인 그리드 -->
          <div class="stl-container">

            <!-- 전체(overall) 행 -->
            <div class="stl-row">
              <div class="stl-label">전체</div>
              <div class="stl-bars">
                {#each getPaddedBars('__overall__') as bar}
                  <div
                    class="stl-bar"
                    class:stl-bar-empty={bar === null}
                    style={bar ? `background:${statusColor(bar.status)}` : ''}
                    title={bar ? `${new Date(bar.timestamp).toLocaleString('ko-KR')} · ${statusLabel(bar.status)}` : ''}
                  ></div>
                {/each}
              </div>
              <div class="stl-current" style="color:{statusColor(serverStatus?.overall ?? 'unknown')}">
                ● {statusLabel(serverStatus?.overall ?? 'unknown')}
              </div>
            </div>
            <div class="stl-scale">
              <span>{statusHistory.length}회 확인됨</span>
              <span>최근</span>
            </div>

            <!-- 컴포넌트별 행 -->
            {#each getComponentList() as { key, label }}
              <div class="stl-row">
                <div class="stl-label">{label}</div>
                <div class="stl-bars">
                  {#each getPaddedBars(key) as bar}
                    <div
                      class="stl-bar"
                      class:stl-bar-empty={bar === null}
                      style={bar ? `background:${statusColor(bar.status)}` : ''}
                      title={bar ? `${new Date(bar.timestamp).toLocaleString('ko-KR')} · ${statusLabel(bar.status)}` : ''}
                    ></div>
                  {/each}
                </div>
                <div class="stl-current" style="color:{statusColor(latestComponentStatus(key))}">
                  ● {statusLabel(latestComponentStatus(key))}
                </div>
              </div>
              <div class="stl-scale stl-scale-comp">
                <span></span><span></span>
              </div>
            {/each}
          </div>

          <!-- 범례 -->
          <div class="stl-legend">
            <span class="stl-legend-item"><span class="stl-legend-dot" style="background:#22c55e"></span>정상</span>
            <span class="stl-legend-item"><span class="stl-legend-dot" style="background:#f59e0b"></span>저하</span>
            <span class="stl-legend-item"><span class="stl-legend-dot" style="background:#ef4444"></span>오류</span>
            <span class="stl-legend-item"><span class="stl-legend-dot" style="background:#94a3b8"></span>알 수 없음</span>
          </div>

          <p class="timeline-hint">막대 위에 마우스를 올리면 확인 시각과 상태를 볼 수 있습니다. 서버가 2시간마다 자동 기록하며 최근 90회가 표시됩니다.</p>

        {:else}
          <!-- 첫 확인 대기 상태 -->
          <div class="status-init-state">
            <div class="loading">서버 상태를 확인하고 있습니다...</div>
          </div>
        {/if}
      </section>
    {/if}

  </main>
</div>

<style>
  .api-docs-page {
    display: flex;
    gap: 0;
    min-height: calc(100vh - 70px);
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
  }

  /* ── 사이드바 ── */
  .sidebar {
    width: 220px;
    min-width: 180px;
    flex-shrink: 0;
    padding: 1.5rem 0.75rem 2rem;
    border-right: 1px solid rgba(128,128,128,0.2);
    position: sticky;
    top: 70px;
    height: calc(100vh - 70px);
    overflow-y: auto;
  }
  .sidebar-title {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    opacity: 0.5;
    margin: 0 0.5rem 1rem;
  }
  .sidebar nav {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .sidebar button {
    display: block;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 0.5rem 0.7rem;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    font-family: inherit;
    transition: background 0.15s;
    color: inherit;
    opacity: 0.7;
  }
  .sidebar button.active,
  .sidebar button:hover {
    opacity: 1;
    background: rgba(254, 106, 55, 0.12);
    color: #FE6A37;
  }

  /* ── 메인 콘텐츠 ── */
  .doc-content {
    flex: 1;
    padding: 2rem 2.5rem;
    overflow-y: auto;
    min-width: 0;
  }
  .doc-content h1 {
    font-size: 1.7rem;
    margin: 0 0 0.8rem;
  }
  .doc-content h2 {
    font-size: 1.1rem;
    margin: 2rem 0 0.8rem;
    border-bottom: 1px solid rgba(128,128,128,0.2);
    padding-bottom: 0.4rem;
  }
  .lead {
    font-size: 1rem;
    opacity: 0.8;
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }

  /* ── 정보 카드 그리드 ── */
  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }
  .info-card {
    border: 1px solid rgba(128,128,128,0.2);
    border-radius: 12px;
    padding: 1.2rem;
  }
  .info-icon { font-size: 1.5rem; margin-bottom: 0.5rem; }
  .info-card h3 { font-size: 0.95rem; margin: 0 0 0.4rem; }
  .info-card p  { font-size: 0.85rem; opacity: 0.75; margin: 0; line-height: 1.5; }

  /* ── 엔드포인트 박스 ── */
  .endpoint-box {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: rgba(128,128,128,0.1);
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 1.5rem;
    font-family: monospace;
  }
  .method { font-weight: 700; padding: 0.2rem 0.5rem; border-radius: 4px; font-size: 0.85rem; color: #fff; }
  .method.post { background: #22c55e; }

  /* ── 코드 패널 (탭 + 코드 통합) ── */
  .code-panel {
    border: 1px solid rgba(254,106,55,0.25);
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 1.5rem;
    background: rgba(0,0,0,0.28);
  }

  /* ── 코드 탭 바 ── */
  .code-tabs {
    display: flex;
    align-items: stretch;
    background: rgba(254,106,55,0.06);
    border-bottom: 1px solid rgba(254,106,55,0.2);
    padding: 0 0.25rem;
  }
  .code-tabs button {
    padding: 0.55rem 1.1rem;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    cursor: pointer;
    font-size: 0.82rem;
    font-family: inherit;
    color: inherit;
    opacity: 0.45;
    transition: opacity 0.15s, border-color 0.15s, color 0.15s;
    margin-bottom: -1px;
  }
  .code-tabs button:hover { opacity: 0.8; }
  .code-tabs button.tab-active {
    opacity: 1;
    border-bottom-color: #FE6A37;
    color: #FE6A37;
    font-weight: 600;
  }

  /* 탭 바 내 복사 버튼 */
  .copy-btn-tab {
    margin-left: auto;
    padding: 0.3rem 0.75rem;
    align-self: center;
    background: rgba(254,106,55,0.08);
    border: 1px solid rgba(254,106,55,0.25);
    border-radius: 6px;
    color: rgba(254,106,55,0.7);
    font-size: 0.75rem;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    font-family: inherit;
    line-height: 1.4;
    user-select: none;
    white-space: nowrap;
  }
  .copy-btn-tab:hover {
    background: rgba(254,106,55,0.18);
    border-color: rgba(254,106,55,0.5);
    color: #FE6A37;
  }
  .copy-btn-tab:active { background: rgba(254,106,55,0.28); }

  /* ── 코드 블록 (단독 / 패널 내부 공용) ── */
  .code-block {
    border-radius: 10px;
    overflow: auto;
    margin-bottom: 1.5rem;
    background: rgba(0,0,0,0.28);
    border: 1px solid rgba(128,128,128,0.18);
  }
  .code-panel .code-block {
    border: none;
    border-radius: 0;
    margin-bottom: 0;
    background: transparent;
  }
  .code-block pre {
    margin: 0;
    padding: 1.2rem;
    font-size: 0.82rem;
    line-height: 1.65;
    font-family: 'Fira Code', 'Cascadia Code', monospace;
    white-space: pre;
    overflow-x: auto;
    color: #e0e0e0;
  }
  :global(.light) .code-block { background: #1e1e2e; }
  :global(.light) .code-block pre { color: #e0e0e0; }

  /* ── 응답 예시 코드 블록 (복사 버튼 포함) ── */
  .code-block-wrapper {
    position: relative;
    margin-bottom: 1.5rem;
  }
  .code-block-wrapper .code-block {
    margin-bottom: 0;
  }
  .copy-btn {
    position: absolute;
    top: 0.55rem;
    right: 0.55rem;
    z-index: 10;
    padding: 0.22rem 0.65rem;
    background: rgba(254,106,55,0.08);
    border: 1px solid rgba(254,106,55,0.25);
    border-radius: 6px;
    color: rgba(254,106,55,0.7);
    font-size: 0.75rem;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    font-family: inherit;
    line-height: 1.4;
    user-select: none;
  }
  .copy-btn:hover {
    background: rgba(254,106,55,0.18);
    border-color: rgba(254,106,55,0.5);
    color: #FE6A37;
  }
  .copy-btn:active { background: rgba(254,106,55,0.28); }

  /* ── 표 ── */
  .param-table, .response-table, .usage-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }
  .param-table th, .response-table th, .usage-table th {
    text-align: left;
    padding: 0.5rem 0.75rem;
    background: rgba(128,128,128,0.1);
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .param-table td, .response-table td, .usage-table td {
    padding: 0.55rem 0.75rem;
    border-top: 1px solid rgba(128,128,128,0.15);
  }
  .required { color: #FE6A37; font-weight: 600; font-size: 0.8rem; }

  /* ── 배지 ── */
  .badge {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    font-size: 0.78rem;
    font-weight: 700;
    color: #fff;
  }
  .badge.green  { background: #16a34a; }
  .badge.blue   { background: #2563eb; }
  .badge.red    { background: #dc2626; }
  .badge.yellow { background: #d97706; }

  /* ── 스타일 칩 ── */
  .style-chips { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.5rem; }
  .chip {
    padding: 0.3rem 0.7rem;
    border-radius: 20px;
    border: 1px solid rgba(254, 106, 55, 0.4);
    font-size: 0.82rem;
    color: #FE6A37;
  }

  /* ── 알림 ── */
  .alert {
    padding: 0.9rem 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }
  .alert.info    { background: rgba(59,130,246,0.12); border: 1px solid rgba(59,130,246,0.3); color: #93c5fd; }
  .alert.error   { background: rgba(239,68,68,0.1);   border: 1px solid rgba(239,68,68,0.3);  color: #fca5a5; }
  .alert.success { background: rgba(254,106,55,0.1);  border: 1px solid rgba(254,106,55,0.4); color: #FE6A37; }

  /* ── 키 생성 카드 ── */
  .create-key-card {
    border: 1px solid rgba(128,128,128,0.2);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
  .create-key-card h2 { margin: 0 0 0.4rem; border: none; font-size: 1rem; }
  .hint { font-size: 0.85rem; opacity: 0.65; margin: 0 0 1rem; }
  .create-key-form {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .create-key-form input {
    flex: 1;
    min-width: 200px;
    padding: 0.6rem 1rem;
    border-radius: 8px;
    border: 1px solid rgba(128,128,128,0.3);
    background: rgba(128,128,128,0.08);
    color: inherit;
    font-family: inherit;
    font-size: 0.9rem;
  }
  .create-key-form input:disabled { opacity: 0.5; }
  .warn-text { font-size: 0.82rem; color: #f59e0b; margin-top: 0.5rem; }

  /* ── 버튼 ── */
  .btn-primary {
    padding: 0.6rem 1.4rem;
    background: #FE6A37;
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    font-family: inherit;
    font-weight: 600;
    transition: opacity 0.15s;
  }
  .btn-primary:hover:not(:disabled) { opacity: 0.85; }
  .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-danger {
    padding: 0.4rem 0.9rem;
    background: rgba(239,68,68,0.15);
    color: #f87171;
    border: 1px solid rgba(239,68,68,0.3);
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    font-family: inherit;
    transition: background 0.15s;
  }
  .btn-danger:hover { background: rgba(239,68,68,0.25); }
  .btn-icon {
    padding: 0.35rem 0.75rem;
    background: rgba(128,128,128,0.1);
    border: 1px solid rgba(128,128,128,0.2);
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.78rem;
    font-family: inherit;
    color: inherit;
    white-space: nowrap;
    transition: background 0.15s;
  }
  .btn-icon:hover { background: rgba(128,128,128,0.2); }

  /* ── 키 목록 ── */
  .key-list { display: flex; flex-direction: column; gap: 0.85rem; margin-bottom: 1.5rem; }
  .key-card {
    border: 1px solid rgba(128,128,128,0.2);
    border-radius: 10px;
    padding: 0.65rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: nowrap;
  }
  .key-name { font-weight: 600; font-size: 0.9rem; white-space: nowrap; flex-shrink: 0; }
  .key-meta { font-size: 0.75rem; opacity: 0.5; white-space: nowrap; flex-shrink: 0; margin-left: auto; }
  .key-value {
    font-family: monospace;
    font-size: 0.82rem;
    background: rgba(128,128,128,0.1);
    padding: 0.25rem 0.65rem;
    border-radius: 6px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* ── 통계 ── */
  .stats-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
  }
  .stat-card {
    flex: 1;
    min-width: 120px;
    border: 1px solid rgba(128,128,128,0.2);
    border-radius: 12px;
    padding: 1.2rem;
    text-align: center;
  }
  .stat-value { font-size: 2rem; font-weight: 700; color: #FE6A37; }
  .stat-label { font-size: 0.8rem; opacity: 0.6; margin-top: 0.3rem; }

  /* ── 서버 상태 ── */
  .overall-status {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    border: 2px solid;
    border-radius: 12px;
    padding: 1rem 1.2rem;
    margin-bottom: 1.8rem;
    flex-wrap: wrap;
  }
  .overall-dot {
    width: 10px; height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-block;
  }
  .overall-text { font-weight: 600; flex: 1; }
  .updated-at { font-size: 0.78rem; opacity: 0.5; }

  /* ── 상태 타임라인 ── */
  .stl-container { margin-bottom: 1.5rem; }
  .stl-row {
    display: flex;
    align-items: stretch;
    gap: 0.75rem;
    margin-bottom: 0.3rem;
  }
  .stl-label {
    width: 130px;
    min-width: 130px;
    font-size: 0.85rem;
    font-weight: 600;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
  .stl-bars {
    flex: 1;
    display: flex;
    gap: 2px;
    height: 34px;
  }
  .stl-bar {
    flex: 1;
    border-radius: 3px;
    min-width: 3px;
    cursor: default;
    transition: opacity 0.1s;
  }
  .stl-bar:hover { opacity: 0.72; }
  .stl-bar-empty {
    background: rgba(128,128,128,0.13) !important;
    cursor: default;
  }
  .stl-current {
    width: 72px;
    min-width: 72px;
    font-size: 0.8rem;
    font-weight: 600;
    text-align: right;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .stl-scale {
    display: flex;
    justify-content: space-between;
    font-size: 0.72rem;
    opacity: 0.42;
    padding: 0.1rem 0 0.9rem;
    padding-left: calc(130px + 0.75rem);
    padding-right: calc(72px + 0.75rem);
  }
  .stl-scale-comp { padding-bottom: 0.4rem; }
  .stl-legend {
    display: flex;
    gap: 1.25rem;
    margin-bottom: 0.8rem;
    font-size: 0.82rem;
    opacity: 0.7;
    flex-wrap: wrap;
  }
  .stl-legend-item { display: flex; align-items: center; gap: 0.4rem; }
  .stl-legend-dot { width: 12px; height: 12px; border-radius: 3px; display: inline-block; flex-shrink: 0; }
  .timeline-hint { font-size: 0.78rem; opacity: 0.45; margin-bottom: 1rem; }
  .status-init-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 3rem 2rem;
    text-align: center;
    opacity: 0.7;
  }

  /* ── 공통 ── */
  .loading { padding: 2rem; text-align: center; opacity: 0.6; }
  .no-keys-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 3rem 2rem;
    border: 1px dashed rgba(128,128,128,0.3);
    border-radius: 12px;
    text-align: center;
  }
  .no-keys-state .no-keys-icon { font-size: 2.5rem; }
  .no-keys-state h3 { margin: 0; font-size: 1.05rem; }
  .no-keys-state p  { margin: 0; font-size: 0.88rem; opacity: 0.65; }
  .empty-state {
    padding: 2rem;
    text-align: center;
    opacity: 0.55;
    border: 1px dashed rgba(128,128,128,0.3);
    border-radius: 10px;
    font-size: 0.9rem;
  }
  .info-note {
    background: rgba(128,128,128,0.08);
    border-left: 3px solid #FE6A37;
    padding: 0.8rem 1rem;
    border-radius: 0 8px 8px 0;
    font-size: 0.85rem;
    line-height: 1.6;
    margin-top: 1rem;
  }

  code { font-family: monospace; font-size: 0.88em; }

  @media (max-width: 680px) {
    .api-docs-page { flex-direction: column; }
    .sidebar {
      width: 100%;
      min-width: unset;
      height: auto;
      position: static;
      border-right: none;
      border-bottom: 1px solid rgba(128,128,128,0.2);
      padding: 1rem;
    }
    .sidebar nav { flex-direction: row; flex-wrap: wrap; }
    .doc-content { padding: 1.2rem 1rem; }
  }
</style>
